import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Shield, Users, Truck, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: "admin",     label: "Admin",     Icon: Shield, color: "#DC2626", description: "Full control, insights, exports, and management tools." },
  { id: "logistics", label: "Logistics", Icon: Truck,  color: "#2563EB", description: "Approvals, stock movement coordination, and operational follow-up." },
  { id: "staff",     label: "Staff",     Icon: Users,  color: "#16a34a", description: "Fast stock lookup, reservations visibility, and daily sales support." },
];

export default function LoginPage({ onLogin }) {
  const [activeRole, setActiveRole] = useState(null);
  const [password, setPassword]     = useState("");
  const [email, setEmail]           = useState("");
  const [loading, setLoading]       = useState(false);

  const handleRoleSelect = (roleId) => {
    setActiveRole(roleId);
    setPassword("");
    setEmail("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeRole) { toast.error("Please select a role first"); return; }

    if (activeRole === "staff") {
      if (!email.trim()) { toast.error("Please enter your email address"); return; }
      setLoading(true);
      try {
        await axios.post(`${API}/auth/staff-email`, { email: email.trim() }, { withCredentials: true });
        toast.success("Welcome! Logged in as Staff");
        localStorage.setItem("auth", "true");
        localStorage.setItem("role", "staff");
        onLogin("staff");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Email not recognised. Contact your manager.");
      } finally {
        setLoading(false);
      }
    } else {
      if (!password) { toast.error("Please enter your password"); return; }
      setLoading(true);
      try {
        const res = await axios.post(`${API}/auth`, { password }, { withCredentials: true });
        if (res.data.success) {
          const role = res.data.role;
          toast.success(`Welcome! Logged in as ${role}`);
          localStorage.setItem("auth", "true");
          localStorage.setItem("role", role);
          onLogin(role);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Authentication failed");
      } finally {
        setLoading(false);
      }
    }
  };

  const selectedRole = ROLES.find(r => r.id === activeRole);

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#080808] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(220,38,38,0.34),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(199,146,0,0.20),transparent_28%),linear-gradient(135deg,#070707_0%,#1a0b0b_42%,#09090b_100%)]" />
      <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="absolute -top-16 left-[-8%] h-64 w-64 rounded-full bg-[#DC2626]/15 blur-3xl" />
      <div className="absolute bottom-0 right-[-8%] h-80 w-80 rounded-full bg-[#C79200]/10 blur-3xl" />

      <div className="relative min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-6xl grid lg:grid-cols-[1.15fr_0.95fr] gap-6 items-stretch animate-fade-in-up">
          <section className="hidden lg:flex flex-col justify-between rounded-[32px] border border-white/10 bg-white/6 backdrop-blur-xl p-8 xl:p-10 shadow-[0_25px_80px_rgba(0,0,0,0.35)]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-white/80">
                <Sparkles className="h-3.5 w-3.5 text-[#FCA5A5]" />
                Premium inventory access
              </div>
              <div className="mt-8 flex items-center gap-4">
                <img src="/logo.jpeg" alt="Red Magma" className="h-16 w-16 rounded-2xl object-contain bg-white/95 p-2 shadow-lg" />
                <div>
                  <p className="heading-font text-5xl tracking-tight text-white">RED MAGMA</p>
                  <p className="text-sm text-white/70 mt-1">Inventory intelligence for sales, logistics, and admin operations.</p>
                </div>
              </div>

              <div className="mt-10 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-5">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-white/50">What this portal gives you</p>
                  <p className="mt-3 text-2xl font-semibold text-white leading-tight">Live stock visibility, reservation control, and AI-backed operational clarity.</p>
                </div>

                <div className="grid gap-3">
                  {ROLES.map(({ id, label, Icon, color, description }) => (
                    <div key={id} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 flex items-start gap-3">
                      <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: `${color}22`, color }}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{label}</p>
                        <p className="text-sm text-white/65 leading-relaxed">{description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/25 px-5 py-4 text-sm text-white/65">
              <div>
                <p className="text-white font-semibold">Red Magma General Trading LLC</p>
                <p className="mt-1">Dubai, UAE</p>
              </div>
              <div className="text-right">
                <p className="text-white font-semibold">ERP bridge</p>
                <p className="mt-1">PACT sync every 5 minutes</p>
              </div>
            </div>
          </section>

          <div className="rounded-[32px] border border-white/15 bg-white/92 text-[#09090b] backdrop-blur-2xl shadow-[0_25px_80px_rgba(0,0,0,0.35)] overflow-hidden">
            <div className="border-b border-black/5 bg-[linear-gradient(135deg,rgba(220,38,38,0.12),rgba(255,255,255,0.7),rgba(199,146,0,0.10))] px-6 py-6 sm:px-8">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#DC2626]/60">Red Magma</p>
                  <h1 className="heading-font text-4xl sm:text-5xl tracking-tight text-[#09090b] mt-2">Inventory Access</h1>
                  <p className="text-sm text-[#475569] mt-2 max-w-md">Select your role and continue into the live Red Magma inventory workspace.</p>
                </div>
                <img src="/logo.jpeg" alt="Red Magma" className="h-14 w-14 rounded-2xl object-contain bg-white p-2 shadow-md" />
              </div>
            </div>

            <div className="px-6 py-6 sm:px-8 sm:py-8">
              <div className="lg:hidden rounded-2xl border border-[#fecaca] bg-[#fff1f2] px-4 py-3 mb-6">
                <p className="text-sm font-semibold text-[#7f1d1d]">RED MAGMA</p>
                <p className="text-sm text-[#7f1d1d]/80 mt-1">Live stock visibility, reservation control, and operational clarity in one place.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {ROLES.map(({ id, label, Icon, color, description }) => {
                  const isActive = activeRole === id;
                  return (
                    <button
                      key={id}
                      data-testid={`role-select-${id}`}
                      type="button"
                      onClick={() => handleRoleSelect(id)}
                      className="text-left rounded-2xl border px-4 py-4 transition-all duration-200 select-none shadow-sm hover:-translate-y-0.5"
                      style={{
                        borderColor: isActive ? color : '#E5E7EB',
                        background: isActive ? `linear-gradient(180deg, ${color}18 0%, rgba(255,255,255,0.96) 100%)` : 'rgba(255,255,255,0.88)',
                        boxShadow: isActive ? `0 18px 40px -22px ${color}` : '0 12px 30px -24px rgba(15,23,42,0.35)',
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ backgroundColor: `${color}16`, color }}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: isActive ? color : '#94A3B8' }}>
                          {isActive ? 'Active' : ''}
                        </span>
                      </div>
                      <p className="mt-4 text-sm font-semibold uppercase tracking-wide" style={{ color: isActive ? color : '#0F172A' }}>{label}</p>
                      <p className="mt-2 text-xs text-[#64748b] leading-relaxed min-h-[48px]">{description}</p>
                    </button>
                  );
                })}
              </div>

              {activeRole && (
                <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up">
                  <div className="rounded-2xl border border-black/5 bg-[#f8fafc] px-4 py-3">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#94A3B8]">Selected role</p>
                    <p className="mt-1 text-sm font-semibold" style={{ color: selectedRole?.color }}>{selectedRole?.label}</p>
                  </div>

                  {activeRole === "staff" ? (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748b]">Work email</label>
                      <Input
                        data-testid="login-email-input"
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="h-[54px] text-base border border-black/10 bg-white rounded-2xl px-4 shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2"
                        style={{ "--tw-ring-color": selectedRole?.color }}
                        autoFocus
                        autoComplete="email"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64748b]">Password</label>
                      <Input
                        data-testid="login-password-input"
                        type="password"
                        placeholder={`Enter ${selectedRole?.label} password`}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="h-[54px] text-base border border-black/10 bg-white rounded-2xl px-4 shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2"
                        style={{ "--tw-ring-color": selectedRole?.color }}
                        autoFocus
                        autoComplete="current-password"
                      />
                    </div>
                  )}

                  <Button
                    data-testid="login-submit-button"
                    type="submit"
                    disabled={loading}
                    className="w-full min-h-[54px] text-white font-semibold uppercase tracking-[0.24em] text-sm rounded-2xl shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)] hover:opacity-95"
                    style={{ background: `linear-gradient(135deg, ${selectedRole?.color} 0%, #09090b 160%)` }}
                  >
                    {loading ? "AUTHENTICATING..." : "ENTER"}
                  </Button>
                </form>
              )}

              {!activeRole && (
                <p className="text-center text-sm text-[#64748b] py-4" data-testid="login-role-helper-text">
                  Select your role above to sign in.
                </p>
              )}

              <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-start gap-3">
                  <Lock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 leading-relaxed">Do not share passwords. All logins are monitored and recorded.</p>
                </div>
              </div>

              <div className="mt-6 text-center text-xs text-[#64748b]">
                <p>Red Magma General Trading LLC</p>
                <p className="mt-1">Dubai, UAE</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
